// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-enum-comparison, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unused-vars, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import {
  getPriceLines,
  toEntries,
  update,
  pruneLatest,
  getLast,
  levelsDateUpdated,
  getEquitiesBySyms,
  getQueryDate,
  sigHighLow,
} from '../../util';
import Series from '../hiro/HiroChart/trading_view/Series';
import Chart from '../hiro/HiroChart/trading_view/Chart';
import PriceLine from '../hiro/HiroChart/trading_view/PriceLine';
import { BaselineSeries, LineSeries, LineStyle } from 'lightweight-charts';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HomeQuadrantTabId,
  PriceLineKey,
  SigHighLowData,
  SymSelectorSettings,
} from '../../types';
import poll from '../../util/poll';
import { useRecoilValue } from 'recoil';
import {
  negativeTrendColorState,
  positiveTrendColorState,
  todaysOpenArrState,
  timezoneState,
  workerState,
} from '../../states';
import useLog from '../../hooks/useLog';
import convertData from '../hiro/HiroChart/convertData';
import { ComboSymbol } from '../../config';
import { Loader } from '../shared';
import { Box, Typography } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { convertLevelsToFutures } from '../../util/home';
import { Center } from '../shared/Center';
import useHomeContent from 'hooks/home/useHomeContent';
import { IndicesHeader } from 'components/indices';

const POLL_INTERVAL = 10_000;

interface FuturesChartProps {
  selectedSym: string;
  symSelectorSettings?: SymSelectorSettings;
  containerStyleOverrides?: SxProps<Theme>;
  headerExtras?: React.ReactNode;
}

export const FuturesChart = ({
  selectedSym,
  symSelectorSettings,
  containerStyleOverrides,
  headerExtras,
}: FuturesChartProps) => {
  const theme = useTheme();
  const [api, setApi] = useState(null);
  const handleRef = useCallback(setApi, [setApi]);
  const priceSeriesRef = useRef(null);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sgData, setSgData] = useState<any>(undefined);
  const worker = useRecoilValue(workerState);
  const currentTimezone = useRecoilValue(timezoneState);
  const { fetchAPIWithLog } = useLog('FuturesChart');
  const posColor = useRecoilValue(positiveTrendColorState);
  const negColor = useRecoilValue(negativeTrendColorState);
  const isMobile = useIsMobile();
  const todaysOpenArr = useRecoilValue(todaysOpenArrState);
  const [futuresDiff, setFuturesDiff] = useState<number>(0);
  const [sigData, setSigData] = useState<SigHighLowData | undefined>(undefined);

  const pricesDataRef = useRef<any[]>([]);
  const priceLines = getPriceLines(sgData, theme, sigData, true);
  const { getKeyLevelsData } = useHomeContent();

  const lastClose = sgData?.upx;

  const fetchData = async () => {
    setLoading(true);
    const [rawData, newSgData, rawKeyLevelsData] = await Promise.all([
      fetchAPIWithLog(`v1/futures?sym=${encodeURIComponent(selectedSym)}`),
      getEquitiesBySyms(
        getQueryDate(true),
        selectedSym === ComboSymbol.ES_F ? 'SPX' : selectedSym,
      ),
      getKeyLevelsData(),
    ]);

    const spxKeyLevelsData = rawKeyLevelsData?.data?.find(
      (k: any) => k.sym === 'SPX',
    );

    if (selectedSym === ComboSymbol.ES_F) {
      // update futures diff
      if (spxKeyLevelsData?.futuresDiff != null) {
        setFuturesDiff(spxKeyLevelsData.futuresDiff);
      }

      // if we are using levels data from a day that isn't yesterday, then the futures chart will be inaccurate, so dont use it.
      if (levelsDateUpdated(newSgData?.trade_date)) {
        const convertedSgData = convertLevelsToFutures({
          ...newSgData,
          futuresDiff: spxKeyLevelsData.futuresDiff,
        });
        setSgData(convertedSgData);
      }
    } else {
      setSgData(newSgData);
      setFuturesDiff(0);
    }

    const symData = rawData ? rawData[selectedSym] : undefined;
    if (symData?.all?.length ?? 0 > 0) {
      const data = convertData(symData, true, currentTimezone);
      pricesDataRef.current = toEntries(
        data.all.TOT,
        'stock_price',
        currentTimezone,
      );
    } else {
      setErrored(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    const newSigData = sigHighLow(sgData, todaysOpenArr);
    if (newSigData == null) {
      return;
    }

    setSigData({
      sig_high: newSigData.sig_high + futuresDiff,
      sig_low: newSigData.sig_low + futuresDiff,
    });
  }, [todaysOpenArr, sgData, futuresDiff]);

  useEffect(() => {
    fetchData();
  }, [selectedSym]);

  const handleLatestResponse = ({ json }: any) => {
    if (pricesDataRef.current.length === 0) {
      return;
    }

    const fetchedData: any = convertData(
      json[selectedSym],
      true,
      currentTimezone,
    );

    const latestPrices = toEntries(
      fetchedData.all.TOT,
      'stock_price',
      currentTimezone,
    );

    const newStockPrices = pruneLatest(
      getLast(pricesDataRef.current),
      pricesDataRef.current,
      latestPrices,
      'time',
    );

    if (newStockPrices.length === 0) {
      return;
    }

    pricesDataRef.current.push(...newStockPrices);

    update(priceSeriesRef.current, newStockPrices);
  };

  useEffect(() => {
    return poll(worker, {
      url: `v1/futures/realtime?sym=${encodeURIComponent(selectedSym)}`,
      interval: POLL_INTERVAL,
      onResponse: handleLatestResponse,
    });
  }, [worker, selectedSym]);

  const len = pricesDataRef.current.length;

  const timeScale = {
    timeVisible: true,
    secondsVisible: true,
    shiftVisibleRangeOnNewBar: true,
    logicalRange: [0, len - 1],
    fixLeftEdge: true,
    fixRightEdge: true, // false (desirable) prevents zooming out an arbitrary amount?
  };

  const chartLayoutOptions = {
    background: {
      color: theme.palette.background.paper,
    },
    textColor: theme.palette.text.primary,
  };

  const renderChart = () => {
    const colorProps =
      lastClose != null
        ? {
            topLineColor: posColor,
            topFillColor: posColor,
            bottomLineColor: negColor,
            bottomFillColor: negColor,
          }
        : { color: theme.palette.hiro.lenses.price.total };

    return (
      <Chart
        height={1}
        rightPriceScale={{
          autoScale: true,
          visible: true,
        }}
        leftPriceScale={{
          visible: true,
          scaleMargins: { top: 0.3, bottom: 0.25 },
        }}
        ref={handleRef}
        layout={chartLayoutOptions}
        timeScale={timeScale}
      >
        <Series
          ref={priceSeriesRef}
          data={pricesDataRef.current}
          baseValue={{ type: 'price', price: lastClose ?? 0 }}
          name="Price"
          priceScaleId={'left'}
          type={lastClose != null ? BaselineSeries : LineSeries}
          lineWidth={3}
          {...colorProps}
        >
          {Object.keys(priceLines)
            .filter((k: string) => priceLines[k]?.value != null)
            .map((k: string) => (
              <PriceLine
                key={k}
                price={priceLines[k].value}
                color={priceLines[k].color}
                lineStyle={LineStyle.Dotted}
                axisLabelVisible={true}
                title={k}
              />
            ))}
        </Series>
      </Chart>
    );
  };

  return (
    <div
      style={{
        // mobile needs an exact height in order to render properly
        height: isMobile ? '400px' : '98%',
        maxHeight: '100%',
        width: '100%',
      }}
    >
      <Loader isLoading={loading}>
        {errored ? (
          <Center
            sx={{
              cursor: 'pointer',
            }}
          >
            <Typography onClick={fetchData} sx={{ fontSize: '14px' }}>
              Unfortunately, there was an error. Click here to try again.
            </Typography>
          </Center>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              gap: '8px',
              ...containerStyleOverrides,
            }}
          >
            <IndicesHeader
              expandable
              type={HomeQuadrantTabId.LIVE_PRICES_CHART}
              title="Live Prices"
              symbol={selectedSym}
              symSelectorSettings={symSelectorSettings}
              headerExtras={headerExtras}
            />
            {renderChart()}
          </Box>
        )}
      </Loader>
    </div>
  );
};
